<template>
  <component-wrapper class="overview-teasers-fi">
    <grid-container-fi :container="false">
      <div class="fi-col-span-full">
        <tab-navigation-fi
          v-if="activateFilters"
          class="overview-teasers-fi__tab-navigation"
          :tab-navigation="tabNavigation || []"
          @change="(value) => tabChanged(value)"
        />
      </div>
    </grid-container-fi>
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12">
        <!-- eslint-disable vue/no-v-html -->
        <h3
          v-if="isEditMode"
          class="overview-teasers-fi__headline"
          v-html="headlineField?.editable"
        />
        <h3
          v-if="!isEditMode && headlineField?.value"
          class="overview-teasers-fi__headline"
          v-html="headlineField.value"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </grid-container-fi>
    <grid-container-fi :container="false">
      <div class="fi-col-span-full">
        <transition
          enter-active-class="animated fadeIn fastest"
          leave-active-class="animated fadeOut fastest"
          mode="out-in"
        >
          <div
            v-if="teaserList"
            :key="currentTag ? currentTag.id : 0"
          >
            <div class="overview-teasers-fi__teasers">
              <loading-indicator
                v-if="isLoading"
                variant="spinnerLocal"
                class="spinner-local--rebrush"
                :dark="true"
              />

              <template v-for="(teaser, index) in teaserList">
                <teaser-big-image-element-fi
                  v-if="teaser.Document"
                  :key="teaser.Document.ItemLink"
                  :image="
                    teaser.Document.Thumbnail
                      ? { src: teaser.Document.Thumbnail, alt: teaser.Document.TeaserTitle }
                      : null
                  "
                  :image-pos-right="index % 2 !== 0"
                  bg-color="light"
                >
                  <template #content>
                    <template v-if="teaser.Document.TagNames">
                      <span
                        v-for="tag in teaser.Document.TagNames.split('|')"
                        :key="tag"
                        class="badge-fi"
                      >
                        {{ tag }}
                      </span>
                    </template>
                    <h3 v-if="teaser.Document.TeaserTitle">
                      {{ teaser.Document.TeaserTitle }}
                    </h3>
                    <p v-if="teaser.Document.PreviewText">
                      {{ teaser.Document.PreviewText }}
                    </p>
                    <button-fi
                      v-if="teaser.Document.ItemLink && fields.teaserButtonText?.value"
                      :href="teaser.Document.ItemLink"
                      class="button-fi__tertiary"
                      natural-events
                    >
                      {{ fields.teaserButtonText.value }}
                    </button-fi>
                  </template>
                </teaser-big-image-element-fi>
              </template>
            </div>

            <section-divider-fi />

            <pagination-progress-bar
              :pagination-page="page + 1"
              :pagination-total="teaserCount"
              :progressbar-value="pagingPercentage"
              :disabled-state="isLoading"
              :show-more-button="teaserList.length < teaserCount"
              :query-params="null"
              @loadmore="fetchMore"
            >
              <template v-if="(page + 1) * pageSize <= teaserCount">
                <span>{{ loadMoreDict((page + 1) * pageSize, teaserCount) }}</span>
              </template>

              <template v-else>
                <span>{{ loadMoreDict(teaserCount, teaserCount) }}</span>
              </template>
            </pagination-progress-bar>

            <section-divider-fi />
          </div>
        </transition>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { ButtonFi, LoadingIndicator, PaginationProgressBar } from 'atoms';
import { computed, onMounted, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import TabNavigationFi from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import TeaserBigImageElementFi from '@/components/teaser/teaser-big-image-element-fi/TeaserBigImageElementFi.vue';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import type { NewsOverviewFiFields, TeaserOverviewFiFields } from '@/generated/fields';
import SectionDividerFi from '@/pages/section-divider-fi/SectionDividerFi.vue';

defineOptions({ name: 'OverviewTeasersFi' });

const props = withDefaults(
  defineProps<{
    fields?: NewsOverviewFiFields | TeaserOverviewFiFields;
    apiUrl: string;
    customType?: string | null;
  }>(),
  {
    fields: () => ({}) as NewsOverviewFiFields,
    customType: null,
  },
);

const fischer = useFischer();
const dict = useDict();

const pageSize = 9;

// `headline`, `type` and `usePageId` only exist on TeaserOverviewFiFields,
// so narrow the union before reading them.
const headlineField = computed(() =>
  'headline' in props.fields ? props.fields.headline : undefined,
);

const isLoading = ref(true);
const currentTag = ref<any>(null);
const teaserList = ref<any[]>([]);
const teaserCount = ref(0);
const page = ref(0);

const type = computed(
  () => props.customType || ('type' in props.fields ? props.fields.type?.value : undefined),
);
const allTag = computed(() => ({
  id: 0,
  text: props.fields?.allTagsLabel?.value,
}));
const tags = computed(() => {
  if (props.fields?.tags?.length) {
    return props.fields.tags.filter(
      (tag: any) => tag.fields?.tagName?.value && tag.fields.tagName.value !== '',
    );
  }

  return [];
});
const tagsLogicalOr = computed(() => Boolean(props.fields?.tagsLogicalOr?.value));
const tabNavigation = computed(() => {
  if (tags.value.length) {
    const navigation = tags.value.map((tag: any, index: number) => ({
      id: index + 1,
      text: tag.fields?.tagName?.value,
      tagId: tag.id,
    }));

    return [allTag.value, ...navigation];
  }

  return null;
});
const activateFilters = computed(() =>
  Boolean(props.fields?.activateFilters?.value && tabNavigation.value),
);
const activeTag = computed(() => currentTag.value || allTag.value);
const pagingPercentage = computed(() => (100 * teaserList.value.length) / teaserCount.value);

const fetchParams = computed<Record<string, any>>(() => {
  const pageid =
    'usePageId' in props.fields && props.fields.usePageId?.value ? fischer.pageId() : null;
  const lang = fischer.siteLanguage() || null;
  const typeParam = type.value !== props.customType ? type.value : null;
  const email = props.customType === 'NewsPage' ? fischer.user.email() : null;
  let tagsParam = null;

  if (activeTag.value.id === 0 && tags.value?.length) {
    tagsParam = tags.value.map((tag: any) => tag.id).join('|');
  } else if (activeTag.value.id !== 0) {
    tagsParam = activeTag.value.tagId;
  }

  return {
    pageid,
    type: typeParam,
    lang,
    tags: tagsParam,
    tagslogicalor: tagsLogicalOr.value,
    email,
    page: page.value,
    pageSize,
  };
});

function buildUrl() {
  const url = new URL(props.apiUrl, window.location.origin);
  const params = fetchParams.value;
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      url.searchParams.append(key, String(params[key]));
    }
  });
  return url;
}

async function fetchData() {
  const url = buildUrl();

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Can not fetch teaser data: ${response.status}`);
    }
    const result = await response.json();
    if (result?.Data) {
      teaserCount.value = result.Data.TotalSearchResults;
      teaserList.value = result.Data.TeaserList;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

function tabChanged(value: any) {
  isLoading.value = true;
  currentTag.value = value;
  fetchData();
}

async function fetchMore() {
  page.value += 1;

  const url = buildUrl();

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Can not fetch more teaser data: ${response.status}`);
    }
    const result = await response.json();
    if (result?.Data) {
      teaserList.value = [...teaserList.value, ...result.Data.TeaserList];
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

function loadMoreDict(first: number, second: number) {
  const args = [String(first), String(second)];
  switch (type.value) {
    case 'StoryPage':
      return dict.get('teaser-Overview.Pagination.Show-Stories', args);
    case 'ReferencePage':
      return dict.get('teaser-Overview.Pagination.Show-References', args);
    case 'NewsPage':
      return dict.get('teaser-Overview.Pagination.Show-News', args);
    default:
      return dict.get('teaser-Overview.Pagination.Default', args);
  }
}

onMounted(() => {
  fetchData();
});
</script>

<style scoped src="./overview-teasers-fi" />
